import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import TrebbleRadio from "./common/TrebbleRadio";
import TrebbleCheckbox from "./common/TrebbleCheckbox";
import Tooltip from "./common/Tooltip";
import ti18n from 'i18n!nls/Sequenceri18n';

const EditorSearchParamsForSilenceRadio = ({
    searchParams,
    onSearchParamsChange,
    searchWidgetModel,
    labelColorType = "regular", // 'regular' or 'accent'
    isAnalyzing = false // New prop to track analysis state
}) => {
    const SILENCE_THRESHOLD = {
        FAST: 0,
        SMOOTH: 1,
        RELAXED: 2
    };

    const THRESHOLD_CONFIG = [
        {
            key: SILENCE_THRESHOLD.FAST,
            seconds: 0,
            labelKey: 'SILENCE_THRESHOLD_FAST_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_FAST_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_FAST_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_FAST_PLATFORMS'
        },
        {
            key: SILENCE_THRESHOLD.SMOOTH,
            seconds: 1,
            labelKey: 'SILENCE_THRESHOLD_SMOOTH_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_SMOOTH_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_SMOOTH_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_SMOOTH_PLATFORMS'
        },
        {
            key: SILENCE_THRESHOLD.RELAXED,
            seconds: 3,
            labelKey: 'SILENCE_THRESHOLD_RELAXED_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_RELAXED_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_RELAXED_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_RELAXED_PLATFORMS'
        }
    ];

    const getInitialValue = () => {
        // Handle the case where maxSilenceInMilliseconds is specifically 0 (FAST threshold)
        if (searchParams?.maxSilenceInMilliseconds === 0) {
            return SILENCE_THRESHOLD.FAST.toString();
        }
        
        const sec = searchParams?.maxSilenceInMilliseconds ? searchParams.maxSilenceInMilliseconds / 1000 : 1;
        if (sec <= 0) return SILENCE_THRESHOLD.FAST.toString();
        if (sec <= 1) return SILENCE_THRESHOLD.SMOOTH.toString();
        return SILENCE_THRESHOLD.RELAXED.toString();
    };

    const getDefaultValue = () => {
        return SILENCE_THRESHOLD.SMOOTH.toString(); // Default to 1s
    };

    const [value, setValue] = useState(searchParams ? getInitialValue() : getDefaultValue());
    const [isApplying, setIsApplying] = useState(false);
    const [includeUntranscribed, setIncludeUntranscribed] = useState(
        searchWidgetModel?.getIncludeUntranscribed() || false
    );

    useEffect(() => {
        const newValue = getInitialValue();
        setValue(newValue);
    }, [searchParams?.maxSilenceInMilliseconds]);

    const handleChange = async (e) => {
        const newValue = e.target.value;
        setValue(newValue);
        const config = THRESHOLD_CONFIG.find(cfg => cfg.key.toString() === newValue);
        
        if (!config) {
            console.error('Invalid silence threshold selection:', newValue);
            return;
        }
        
        if (!searchWidgetModel || !searchWidgetModel.applySearchResultsForSilencesToTrim) {
            console.warn('searchWidgetModel not available; updating UI only');
            if (onSearchParamsChange) {
                onSearchParamsChange({
                    maxSilenceInMilliseconds: config.seconds * 1000
                });
            }
            return;
        }
        
        setIsApplying(true);
        try {
            await searchWidgetModel.applySearchResultsForSilencesToTrim(
                config.seconds * 1000,
                true,
                false,
                false
            );
            if (onSearchParamsChange) {
                onSearchParamsChange({
                    maxSilenceInMilliseconds: config.seconds * 1000
                });
            }
        } catch (error) {
            console.error('Error applying silence search:', error);
        } finally {
            setIsApplying(false);
        }
    };

    const handleIncludeUntranscribedChange = (e) => {
        const newValue = e.target.checked;
        setIncludeUntranscribed(newValue);
        searchWidgetModel?.setIncludeUntranscribed(newValue);
    };

    const renderRadioOption = (config) => (
        <Tooltip
            key={config.key}
            title={
                <div>
                    <div className="font-bold">{window.getI18n(ti18n, config.ruleLabelKey)}</div>
                    <div className="text-gray-400 mt-1 text-sm">
                        {window.getI18n(ti18n, config.helperKey)} For: {window.getI18n(ti18n, config.platformsKey)}
                    </div>
                </div>
            }
            placement="top"
            autoAdjustOverflow
            useEditorTheme={true}
        >
            <div>
                <TrebbleRadio
                    value={config.key.toString()}
                    style={{ color: 'var(--text-default-color)', margin: '0px', padding: '0px' }}
                >
                    {window.getI18n(ti18n, config.labelKey)}
                </TrebbleRadio>
            </div>
        </Tooltip>
    );

    return (
        <div className="flex flex-col px-2">
            <TrebbleRadio.Group
                onChange={handleChange}
                value={value}
                disabled={isApplying || isAnalyzing}
                className="w-full flex justify-between"
                useEditorTheme={true}
            >
                {THRESHOLD_CONFIG.map(renderRadioOption)}
            </TrebbleRadio.Group>
            
            <div className="flex flex-row items-center" style={{alignItems: 'center'}}>
                <TrebbleCheckbox
                    aria-label={window.getI18n(ti18n, "INCLUDE_UNTRANSCRIBED_CONTENT")}
                    style={{display:"flex",margin:"0px"}}
                    checked={includeUntranscribed}
                    onChange={handleIncludeUntranscribedChange}
                    useEditorTheme={true}
                    id="include-untranscribed-checkbox"
                    disabled={isApplying || isAnalyzing}
                />
                <span htmlFor="include-untranscribed-checkbox" className="text-left ml-2">
                    {window.getI18n(ti18n, "INCLUDE_UNTRANSCRIBED_CONTENT")}
                </span>
                
                <Tooltip title={window.getI18n(ti18n, "INCLUDE_UNTRANSCRIBED_CONTENT_INFO")} placement="top">
                    <button aria-describedby="include-untranscribed-info">
                    <span
                        className="icon pe-7s-info text-lg"
                        tabIndex={0}
                        aria-label={window.getI18n(ti18n, "INCLUDE_UNTRANSCRIBED_CONTENT_INFO")}
                        role="img"
                        style={{ cursor: 'pointer', outline: 'none'}}
                    />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

EditorSearchParamsForSilenceRadio.propTypes = {
    searchParams: PropTypes.object,
    onSearchParamsChange: PropTypes.func,
    searchWidgetModel: PropTypes.object,
    labelColorType: PropTypes.oneOf(["regular", "accent"]),
    isAnalyzing: PropTypes.bool
};

export default EditorSearchParamsForSilenceRadio; 